import type {
  GetAnchorReview,
  GetAnchorReviewSummary,
  PostAnchorReviewConfirm,
  PostAnchorReviewPriceElement,
  PutAnchorReview,
} from "@/server-types";
import { get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { queryClient } from "..";

type AnchorTarget = PostAnchorReviewConfirm["body"]["targets"][number];

function invalidateAnchors() {
  queryClient.invalidateQueries({ queryKey: ["anchor-review"] });
  queryClient.invalidateQueries({ queryKey: ["service-price"] });
}

/** GET /anchor-review — every published price with its anchor and trail. */
export const useGetAnchorReview = () => {
  return useCustomQuery({
    queryKey: ["anchor-review"],
    queryFn: () => get<GetAnchorReview>("/anchor-review"),
  });
};

/** GET /anchor-review/summary — the banner's counts. */
export const useGetAnchorReviewSummary = (enabled = true) => {
  return useCustomQuery({
    queryKey: ["anchor-review", "summary"],
    queryFn: () => get<GetAnchorReviewSummary>("/anchor-review/summary"),
    enabled,
  });
};

/** POST /anchor-review/confirm — confirms anchors as they are. */
export const usePostAnchorReviewConfirm = () => {
  return useCustomMutation({
    mutationKey: ["anchor-review", "confirm"],
    mutationFn: (targets: AnchorTarget[]) =>
      post<PostAnchorReviewConfirm>("/anchor-review/confirm", { targets }),
    onSuccess: invalidateAnchors,
  });
};

/** PUT /anchor-review/:target/:id — corrects an anchor's figure. */
export const usePutAnchorReview = () => {
  return useCustomMutation({
    mutationKey: ["anchor-review", "correct"],
    mutationFn: ({
      target,
      id,
      priceCents,
    }: AnchorTarget & PutAnchorReview["body"]) =>
      put<PutAnchorReview>(`/anchor-review/${target}/${id}`, { priceCents }),
    onSuccess: invalidateAnchors,
  });
};

/** POST /anchor-review/price-element — states a stylist's or role's price on 10.9. */
export const usePostAnchorReviewPriceElement = (
  options: { onSuccess?: () => void } = {},
) => {
  return useCustomMutation({
    mutationKey: ["anchor-review", "price-element"],
    mutationFn: (body: PostAnchorReviewPriceElement["body"]) =>
      post<PostAnchorReviewPriceElement>("/anchor-review/price-element", body),
    onSuccess: () => {
      invalidateAnchors();
      options.onSuccess?.();
    },
  });
};
